/**
 * Shared expert-review visit context — status API + sticky entry `source`.
 *
 * Belongs here: statusData, loading/error flags, entrySource, useExpertReviewStatusContext.
 * Does not belong here: questionnaire answers, navigation, or the entry redirect.
 */
import { createContext, useContext } from 'react'
import type { StatusQuestionnaireApiResponse } from '../types/statusQuestionnaire.types'
import type { QuestionnaireStatus } from '../shared/types/questionnaire.types'

/** Native screen that opened this webview — query `source`. */
export const ExpertReviewSource = {
  PORTFOLIO_DASHBOARD: 'PORTFOLIO_DASHBOARD',
  ELITE_LANDING_DASHBOARD: 'ELITE_LANDING_DASHBOARD',
  ALLOCATION_PLAN_PAGE: 'ALLOCATION_PLAN_PAGE'
} as const

export type ExpertReviewSource =
  (typeof ExpertReviewSource)[keyof typeof ExpertReviewSource]

export interface ExpertReviewStatusContextValue {
  statusData: StatusQuestionnaireApiResponse | undefined
  isStatusLoading: boolean
  isStatusError: boolean
  /** Sticky entry `source` for this visit; null when missing/unknown. */
  entrySource: ExpertReviewSource | null
}

/**
 * Status slice passed into strategy/preferences session hooks for FullFlow hydrate + loading.
 */
export type ExpertReviewStatus = {
  isStatusLoading: boolean
  isStatusError: boolean
  questionnaireStatus: QuestionnaireStatus | null | undefined
}

export const ExpertReviewStatusContext = createContext<ExpertReviewStatusContextValue | undefined>(
  undefined
)

export function useExpertReviewStatusContext() {
  const context = useContext(ExpertReviewStatusContext)
  if (!context) {
    throw new Error('useExpertReviewStatusContext must be used within an ExpertReviewProviders')
  }
  return context
}

/**
 * Epoch ms from status. Null when the field is missing or not a finite number.
 * Cold submitted hub uses this. The pending-visit countdown stays on POST /submit.
 */
export function firstPlanDropTimeFromStatus(
  statusData: StatusQuestionnaireApiResponse | undefined
): number | null {
  const dropTime = statusData?.firstPlanDropTime
  if (typeof dropTime !== 'number' || !Number.isFinite(dropTime)) return null
  return dropTime
}
